import React from 'react';
import { Box, Container, Grid, Typography, useTheme, alpha } from '@mui/material';
import { VALUE_PROPS } from '../../data/siteContent';
import { SECTION_SCROLL_MARGIN } from '../../utils/scroll';
import { MotionBox, fadeInUp, slideIn, useMotionProps } from '../motion';
import SectionHeading from './SectionHeading';

const ABOUT_STATS = [
  { value: '25+', label: 'Years of Excellence' },
  { value: '60+', label: 'Satisfied Clients' }
];

const About = () => {
  const theme = useTheme();
  const motionProps = useMotionProps();

  return (
    <Box
      id="about"
      component="section"
      aria-labelledby="about-heading"
      sx={{ scrollMarginTop: SECTION_SCROLL_MARGIN, py: { xs: 8, md: 12 }, backgroundColor: 'background.paper' }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          id="about-heading"
          eyebrow="Who we are"
          title="About SSIPL"
          subtitle="Since 2000, we have been at the forefront of digital transformation in Sri Lanka, delivering innovative software solutions that empower businesses to thrive."
        />

        <Grid container spacing={{ xs: 5, md: 6 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <MotionBox {...motionProps(slideIn('left'))}>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 2 }}>
                Our Journey
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                Software Systems International (Pvt) Ltd is one of Sri Lanka's most trusted software development
                companies. We build comprehensive enterprise solutions tailored to the unique challenges of Sri Lankan
                businesses.
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                With deep domain knowledge across cooperatives (MPCS), retail, and manufacturing, our systems combine
                international standards with local context.
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                {ABOUT_STATS.map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      textAlign: 'center',
                      py: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                      background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 100%)`
                    }}
                  >
                    <Typography
                      variant="h3"
                      component="p"
                      color="primary"
                      sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </MotionBox>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <MotionBox {...motionProps(slideIn('right'))}>
              <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 2 }}>
                Our Mission
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                Empower Sri Lankan businesses with reliable, scalable software that drives efficiency, enhances customer
                experiences, and accelerates digital transformation.
              </Typography>

              <Box sx={{ display: 'grid', gap: 2 }}>
                {VALUE_PROPS.map(({ icon: Icon, title, description }, i) => (
                  <MotionBox
                    key={title}
                    {...motionProps(fadeInUp(0.1 * i))}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '44px 1fr',
                      columnGap: 2,
                      alignItems: 'start',
                      p: 2,
                      borderRadius: 3,
                      transition: 'background-color 0.25s ease',
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) }
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }}
                    >
                      <Icon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" component="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {title}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {description}
                      </Typography>
                    </Box>
                  </MotionBox>
                ))}
              </Box>
            </MotionBox>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
